/**
 * "What have I queued this session" — purely local, transient, per-device. There's no lookup
 * value in asking the server for this (it's the same list this device already has), so unlike
 * `recently-requested.ts` (a server-enforced cooldown hint) this is the actual source of truth
 * for `MyMusicScreen`, not just a UX shortcut.
 *
 * `title`/`artist` are NOT stored here — they're written once to `trackCache.ts` (deduped by
 * `smId`, shared with `likedTracks.ts`) at the point of request, and resolved from there at
 * render time. Old entries from before this change may still carry those fields in localStorage;
 * they're just inert extra properties now, harmless to leave in place.
 */

const STORAGE_KEY = "touchscreen:my_requests";
const CHANGE_EVENT = "touchscreen:my-requests-changed";
const MAX_ENTRIES = 50;

export interface MyRequestEntry {
  requestId: string;
  smId: string;
  queuePosition: number;
  requestedAtMs: number;
}

// `useSyncExternalStore` requires `getSnapshot` to return a stable reference when nothing has
// changed — otherwise it re-renders, re-reads, sees "another new array", and loops forever. So
// the parsed list is cached and only re-parsed when the raw string actually changes.
let cachedRaw: string | null = null;
let cachedList: MyRequestEntry[] = [];

function readList(): MyRequestEntry[] {
  if (typeof window === "undefined") return cachedList;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return cachedList;
  }
  if (raw === cachedRaw) return cachedList;
  cachedRaw = raw;
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    cachedList = Array.isArray(parsed) ? (parsed as MyRequestEntry[]) : [];
  } catch {
    cachedList = [];
  }
  return cachedList;
}

function writeList(list: MyRequestEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage full or blocked — this is a convenience list, nothing depends on it persisting.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Newest first. Capped so a long session doesn't grow this unbounded. */
export function addMyRequest(entry: Omit<MyRequestEntry, "requestedAtMs">): void {
  const list = readList();
  writeList([{ ...entry, requestedAtMs: Date.now() }, ...list].slice(0, MAX_ENTRIES));
}

export function getMyRequests(): MyRequestEntry[] {
  return readList();
}

/** Notifies same-tab writers (`storage` alone only fires cross-tab) so the screen updates live. */
export function subscribeMyRequests(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}
